"use client";

import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const values = [
{
  icon: "🎸",
  title: "Passionate Teaching",
  desc: "Our instructors are performing musicians who bring real stage experience into every lesson."
},
{
  icon: "🎶",
  title: "Jam Session Culture",
  desc: "We believe music is social. Weekly jam sessions let students collaborate and find their groove together."
},
{
  icon: "🎨",
  title: "Creative Expression",
  desc: "From guitar riffs to art projects — we nurture every creative impulse with structured guidance."
},
{
  icon: "🤝",
  title: "Supportive Community",
  desc: "A welcoming space for beginners and advanced learners alike. No judgment, only growth."
}];


export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el) => {
              el.classList.add("revealed");
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-24 md:py-32 relative" style={{ background: "#111111" }}>
      {/* Top gold divider */}
      <div className="gold-divider mb-0" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center pb-8">
          {/* Left: Image collage */}
          <div className="relative reveal-hidden" style={{ overflow: 'visible' }}>
            <div className="grid grid-cols-2 gap-4">
              <div className="gallery-item rounded-2xl overflow-hidden" style={{ height: "280px" }}>
                <AppImage
                  src="https://images.unsplash.com/photo-1619379042989-393df186bb54"
                  alt="Guitar student practicing chords at Hi-Hats Rajanukunte music academy"
                  className="w-full h-full object-cover" />
                
              </div>
              <div className="gallery-item rounded-2xl overflow-hidden mt-10" style={{ height: "280px" }}>
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_10bdd52bc-1767948121585.png"
                  alt="Students jamming together in a music session at Hi-Hats"
                  className="w-full h-full object-cover" />
                
              </div>
            </div>
            {/* Floating badge */}
            <div
              className="absolute -bottom-6 left-1/2 -translate-x-1/2 badge-yellow px-6 py-3 rounded-full text-sm font-display font-bold whitespace-nowrap"
              style={{ zIndex: 10 }}>
              
              🎵 Est. in Rajanukunte, Bengaluru
            </div>
          </div>

          {/* Right: Content */}
          <div className="space-y-8">
            <div className="reveal-hidden">
              <span
                className="badge-blue px-3 py-1 rounded-full text-xs font-display font-bold uppercase tracking-widest">
                
                About Us
              </span>
              <h2
                className="font-display font-black mt-4 leading-tight tracking-tight"
                style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                
                Not Just Classes —<br />
                <span className="text-yellow">A Musical Movement</span>
              </h2>
              <p
                className="mt-4 leading-relaxed text-base md:text-lg"
                style={{ color: "rgba(255,255,255,0.6)" }}>
                
                Hi-Hats Rajanukunte is where Bangalore's next generation of musicians come to discover their sound. Located in the heart of Rajanukunte, we've built a community
                that blends structured music education with the raw energy of live performance culture.
              </p>
            </div>

            {/* Values grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values?.map((v, i) =>
              <div
                key={v?.title}
                className={`reveal-hidden stagger-${i + 1} rounded-xl p-5 stat-card`}>
                
                  <div className="text-2xl mb-3">{v?.icon}</div>
                  <h4 className="font-display font-bold text-base text-yellow mb-1">{v?.title}</h4>
                  <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>
                    {v?.desc}
                  </p>
                </div>
              )}
            </div>

            <div className="reveal-hidden stagger-4">
              <a
                href="#enroll"
                className="btn-yellow inline-block px-8 py-4 rounded-xl text-sm font-display font-black tracking-wide">
                
                Book a Trial Class →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}